import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { TrendingUpIcon, TargetIcon, ClockIcon, AwardIcon } from 'lucide-react';
import type { Customer } from '@repo/types';
import { ToneBadge } from './_shared';
import { formatCareerLevel } from './utils';

function StatCard({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground">{label}</p>
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        </div>
        <div className="mt-2">{children}</div>
      </CardContent>
    </Card>
  );
}

export function CustomerSummaryStats({ customer }: { customer: Customer }) {
  const completeness = customer.profileCompleteness ?? 0;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard label="Profile Completeness" icon={TrendingUpIcon}>
        <div className="text-2xl font-bold">{completeness}%</div>
        <Progress value={completeness} className="mt-2 h-1.5" />
      </StatCard>

      <StatCard label="Leads Remaining" icon={TargetIcon}>
        <div className="text-2xl font-bold">{customer.leadsRemaining ?? 0}</div>
        {customer.leadsConsumed != null && (
          <p className="mt-0.5 text-xs text-muted-foreground">{customer.leadsConsumed} consumed</p>
        )}
      </StatCard>

      <StatCard label="Experience" icon={ClockIcon}>
        <div className="text-2xl font-bold">
          {customer.yearsOfExperience ?? 'N/A'}
          {customer.yearsOfExperience != null && <span className="text-base font-medium"> yrs</span>}
        </div>
        {customer.careerLevel && (
          <p className="mt-0.5 text-xs text-muted-foreground">{formatCareerLevel(customer.careerLevel)}</p>
        )}
      </StatCard>

      <StatCard label="Subscription" icon={AwardIcon}>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold">
            {customer.subscription ? customer.subscription.plan_name || `Tier ${customer.subscription.tier}` : 'None'}
          </span>
        </div>
        {customer.subscription && (
          <ToneBadge tone={customer.subscription.status === 'active' ? 'success' : 'neutral'} className="mt-1">
            {customer.subscription.status}
          </ToneBadge>
        )}
      </StatCard>
    </div>
  );
}
